import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, MenuItem, Select, Skeleton, Typography } from "@mui/material";

import { colors } from "../../../theme/palette";
import { useNewFollowersStatistics } from "../../../api/queries/dashboard/useQueryNewFollowers";

import GoLineChart from "./GoLineChart";
import { DashboardNewFollowersIcon } from "../../../assets/icons/MUI/DashboardIcons";

const NewFollowers = () => {
  const { t } = useTranslation();

  const [duration, setDuration] = useState<string>("PAST_7_DAYS");
  const durationsList = [
    "PAST_7_DAYS",
    "PAST_14_DAYS",
    "PAST_MONTH",
    "PAST_2_MONTHS",
    "PAST_3_MONTHS",
    "PAST_6_MONTHS",
  ];

  const { statistics, isStatisticsLoading, isStatisticsHasError } = useNewFollowersStatistics(duration);
  const chartData = statistics?.data?.items;
  const totalCount = statistics?.count;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: "30px",
        borderRadius: "8px",
        border: `1px solid #EAECF0`,
        background: colors.white,
        padding: "18px 20px",
        width: "430px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: "18px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            rowGap: "20px",
            flexDirection: {
              sm: "row",
              xs: "column",
            },
          }}
        >
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
              color: colors.Black100,
              fontFamily: "Inter",
              fontSize: "18px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "normal",
            }}
          >
            <DashboardNewFollowersIcon />
            {t("pages.dashboard.newFollowers")}
          </Typography>
          {isStatisticsLoading ? (
            <Skeleton variant="rounded" width="120px" height="34px" animation="wave" />
          ) : (
            <Select
              sx={{
                borderRadius: "8px !important",
                width: {
                  sm: "144px",
                  xs: "100%",
                },
              }}
              value={duration}
              onChange={(event) => setDuration(event.target.value as string)}
            >
              {durationsList.map((duration: string) => (
                <MenuItem value={duration}>{t(`pages.dashboard.${duration}`)}</MenuItem>
              ))}
            </Select>
          )}
        </Box>
        {!isStatisticsHasError && chartData?.length !== 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              columnGap: "12px",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
            }}
          >
            <Typography variant="subtitle1">
              {isStatisticsLoading ? (
                <Skeleton variant="rounded" width="40px" height="15px" animation="wave" />
              ) : (
                totalCount
              )}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.dark600,
                whiteSpace: {
                  sm: "nowrap",
                  xs: "unset",
                },
                display: "flex",
                alignItems: "center",
                columnGap: "8px",
              }}
            >
              {t("pages.dashboard.totalFollowers")}{" "}
              {isStatisticsLoading ? (
                <Skeleton variant="rounded" width="30px" height="15px" animation="wave" />
              ) : (
                t(`pages.dashboard.${duration}`).toLocaleLowerCase()
              )}
            </Typography>
          </Box>
        )}
      </Box>
      {!isStatisticsHasError && (isStatisticsLoading || chartData?.length !== 0) && (
        <GoLineChart chartData={chartData} isLoading={isStatisticsLoading} />
      )}
      {(isStatisticsHasError || chartData?.length === 0) && (
        <Typography variant="body16Regular">{t("pages.dashboard.noStatistics")}</Typography>
      )}
    </Box>
  );
};

export default NewFollowers;
